import React, { useState } from 'react';
import { Menu, X } from 'lucide-react';
interface LayoutProps {
  children: React.ReactNode;
  currentPage: string;
  setCurrentPage: (page: string) => void;
}
export function Layout({
  children,
  currentPage,
  setCurrentPage
}: LayoutProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navItems = [{
    id: 'home',
    label: 'Home'
  }, {
    id: 'about',
    label: 'About & Treatments'
  }, {
    id: 'contact',
    label: 'Contact'
  }];
  const handleNavClick = (pageId: string) => {
    setCurrentPage(pageId);
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };
  return <div className="min-h-screen flex flex-col font-body bg-dark text-slate-300" data-id="element-5">
      {/* Navigation */}
      <header className="bg-dark border-b border-darkCard sticky top-0 z-50" data-id="element-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-7">
          <div className="flex justify-between items-center h-20" data-id="element-8">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center cursor-pointer" onClick={() => handleNavClick('home')} data-id="element-9">
              <span className="font-heading font-bold text-2xl tracking-wide text-white uppercase" data-id="element-10">
                [LOGO] <span className="text-accent" data-id="element-11">[CLINIC NAME]</span>
              </span>
            </div>

            {/* Desktop Menu */}
            <nav className="hidden md:flex items-center space-x-8" data-id="element-12">
              {navItems.map(item => <button key={item.id} onClick={() => handleNavClick(item.id)} className={`text-sm font-semibold uppercase tracking-wider transition-colors ${currentPage === item.id ? 'text-accent' : 'text-slate-400 hover:text-white'}`} data-id="element-13">
                  {item.label}
                </button>)}
              <button onClick={() => handleNavClick('contact')} className="bg-accent hover:bg-accentHover text-white font-bold py-3 px-6 rounded-md transition-all duration-300 shadow-[0_0_15px_rgba(255,107,53,0.3)] hover:shadow-[0_0_25px_rgba(255,107,53,0.5)] hover:-translate-y-0.5 uppercase tracking-wide text-sm" data-id="element-14">
                Schedule your tele-health appointment
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center" data-id="element-15">
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-slate-300 hover:text-white focus:outline-none" data-id="element-16">
                {isMobileMenuOpen ? <X className="h-6 w-6" data-id="element-17" /> : <Menu className="h-6 w-6" data-id="element-18" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && <div className="md:hidden bg-darkSlate border-t border-darkCard" data-id="element-19">
            <div className="px-2 pt-2 pb-4 space-y-1 sm:px-3" data-id="element-20">
              {navItems.map(item => <button key={item.id} onClick={() => handleNavClick(item.id)} className={`block w-full text-left px-3 py-3 rounded-md text-base font-bold uppercase tracking-wide ${currentPage === item.id ? 'bg-darkCard text-accent' : 'text-slate-300 hover:bg-darkCard hover:text-white'}`} data-id="element-21">
                  {item.label}
                </button>)}
              <div className="px-3 pt-4" data-id="element-22">
                <button onClick={() => handleNavClick('contact')} className="w-full bg-accent hover:bg-accentHover text-white font-bold py-3 px-4 rounded-md transition-colors shadow-sm uppercase tracking-wide text-sm" data-id="element-23">
                  Schedule your tele-health appointment
                </button>
              </div>
            </div>
          </div>}
      </header>

      {/* Main Content */}
      <main className="flex-grow" data-id="element-24">{children}</main>

      {/* Footer */}
      <footer className="bg-dark border-t border-darkCard text-slate-400 py-12" data-id="element-25">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-26">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8" data-id="element-27">
            <div data-id="element-28">
              <span className="font-heading font-bold text-xl tracking-wide text-white uppercase mb-4 block" data-id="element-29">
                [CLINIC NAME]
              </span>
              <p className="text-sm text-slate-400" data-id="element-30">[TAGLINE]</p>
            </div>
            <div data-id="element-31">
              <h4 className="text-white font-heading font-bold uppercase tracking-wider mb-4" data-id="element-32">
                Contact
              </h4>
              <ul className="space-y-2 text-sm text-slate-400" data-id="element-33">
                <li data-id="element-34">[PHONE]</li>
                <li data-id="element-35">[EMAIL]</li>
                <li data-id="element-36">Serving [CITY], [STATE]</li>
              </ul>
            </div>
            <div data-id="element-37">
              <h4 className="text-white font-heading font-bold uppercase tracking-wider mb-4" data-id="element-38">
                Legal
              </h4>
              <ul className="space-y-2 text-sm text-slate-400" data-id="element-39">
                <li className="hover:text-accent cursor-pointer transition-colors" data-id="element-40">
                  Terms of Service
                </li>
                <li className="hover:text-accent cursor-pointer transition-colors" data-id="element-41">
                  Privacy Policy
                </li>
                <li className="hover:text-accent cursor-pointer transition-colors" data-id="element-42">
                  Telehealth Consent
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-darkCard pt-8 mt-8" data-id="element-43">
            <div className="bg-darkSlate p-4 rounded-md border border-darkCard" data-id="element-44">
              <p className="text-xs text-slate-400 leading-relaxed text-center" data-id="element-45">
                <strong className="text-accent" data-id="element-46">Important Disclaimer:</strong>{' '}
                This website does not collect medical information. Do not share
                protected health information (PHI) on this site. Medical intake
                and records are handled through our secure telehealth platform.
              </p>
              <p className="text-xs text-slate-500 leading-relaxed text-center mt-2" data-id="element-47">
                [STATE LICENSE TEXT]
              </p>
            </div>
            <p className="text-center text-xs text-slate-500 mt-6" data-id="element-48">
              &copy; {new Date().getFullYear()} [CLINIC NAME]. All rights
              reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}